import { MediaAssetType } from "@videofy/types";
import { Alert, Button, Collapse, Empty, Input, Spin } from "antd";
import MediaAsset from "./MediaAsset";
import { useReactive } from "ahooks";
import { useGlobalState } from "@/state/globalState";
import { GalloAsset, importGalloAsset, searchGalloAssets } from "@/api";

interface ReplaceMediaProps {
  alternativeMedia?: MediaAssetType[];
  onSelectMedia: (asset: MediaAssetType) => void;
}

const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  const maybeAxiosError = error as {
    response?: {
      data?: {
        detail?: unknown;
        error?: unknown;
      };
    };
    message?: unknown;
  };

  const detail = maybeAxiosError.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }

  const detailAsArray = Array.isArray(detail) ? detail : null;
  if (detailAsArray && detailAsArray.length > 0) {
    const joined = detailAsArray
      .map((item) => (typeof item === "string" ? item : JSON.stringify(item)))
      .join("; ")
      .trim();
    if (joined) {
      return joined;
    }
  }

  const apiError = maybeAxiosError.response?.data?.error;
  if (typeof apiError === "string" && apiError.trim()) {
    return apiError;
  }

  if (typeof maybeAxiosError.message === "string" && maybeAxiosError.message.trim()) {
    return maybeAxiosError.message;
  }

  return fallback;
};

const ReplaceMedia = ({
  alternativeMedia,
  onSelectMedia,
}: ReplaceMediaProps) => {
  const { generationId, selectedProject } = useGlobalState();
  const state = useReactive({
    activeKey: undefined as string | string[] | undefined,
    searchText: "",
    loading: false,
    importingAssetId: "",
    searchError: null as string | null,
    importError: null as string | null,
    queried: false,
    assets: [] as GalloAsset[],
  });

  const projectId = generationId || selectedProject?.id || "";

  const loadExternalAssets = async (searchOverride?: string) => {
    if (!projectId) {
      state.searchError = "Select a project before searching external library.";
      return;
    }

    state.loading = true;
    state.searchError = null;
    try {
      const response = await searchGalloAssets(projectId, {
        search_text: searchOverride && searchOverride.trim() ? searchOverride.trim() : undefined,
        media_type: "image",
        collections: ["editorial", "creative"],
        max_queries: 4,
        per_query_limit: 12,
      });
      state.assets = response.assets || [];
      state.queried = true;
    } catch (error) {
      const message = extractApiErrorMessage(error, "Failed to search external library");
      state.searchError = message;
      state.assets = [];
    } finally {
      state.loading = false;
    }
  };

  const handleImportAndSelect = async (asset: GalloAsset) => {
    if (!projectId) {
      state.importError = "Select a project before importing external media.";
      return;
    }

    state.importError = null;
    state.importingAssetId = asset.external_asset_id;
    try {
      const imported = await importGalloAsset(projectId, {
        source_url: asset.source_url,
        file_name: asset.title
          ? `${asset.title.replace(/[^A-Za-z0-9._-]+/g, "-")}.jpg`
          : undefined,
        byline: asset.byline,
        description: asset.description,
        external_asset_id: asset.external_asset_id,
      });

      const mediaPayload = imported.media as {
        url?: unknown;
        byline?: unknown;
        description?: unknown;
        externalAssetId?: unknown;
        provider?: unknown;
        imageAsset?: {
          id?: unknown;
          size?: {
            width?: unknown;
            height?: unknown;
          };
        };
      };

      const widthValue = Number(mediaPayload.imageAsset?.size?.width);
      const heightValue = Number(mediaPayload.imageAsset?.size?.height);

      const selectedAsset: MediaAssetType = {
        type: "image",
        url:
          typeof mediaPayload.url === "string" && mediaPayload.url
            ? mediaPayload.url
            : imported.url,
        byline:
          typeof mediaPayload.byline === "string" ? mediaPayload.byline : undefined,
        description:
          typeof mediaPayload.description === "string"
            ? mediaPayload.description
            : undefined,
        externalAssetId:
          typeof mediaPayload.externalAssetId === "string"
            ? mediaPayload.externalAssetId
            : asset.external_asset_id,
        provider:
          typeof mediaPayload.provider === "string" ? mediaPayload.provider : "gallo",
        imageAsset: {
          id:
            typeof mediaPayload.imageAsset?.id === "string" && mediaPayload.imageAsset.id
              ? mediaPayload.imageAsset.id
              : imported.path,
          size: {
            width: Number.isFinite(widthValue) && widthValue > 0 ? widthValue : 1080,
            height: Number.isFinite(heightValue) && heightValue > 0 ? heightValue : 1080,
          },
        },
      };

      onSelectMedia(selectedAsset);
      state.activeKey = undefined;
    } catch (error) {
      const message = extractApiErrorMessage(error, "Failed to import external media");
      state.importError = message;
    } finally {
      state.importingAssetId = "";
    }
  };

  const handleChangeCollapse = (key: string | string[]) => {
    state.activeKey = key;
    const openKeys = Array.isArray(key) ? key : [key];
    if (openKeys.includes("2") && !state.queried && !state.loading) {
      void loadExternalAssets(state.searchText);
    }
  };

  return (
    <Collapse
      activeKey={state.activeKey}
      onChange={handleChangeCollapse}
      items={[
        {
          key: "1",
          label: "Other media from article",
          children: (
            alternativeMedia && alternativeMedia.length > 0 ? (
              <div className="gap-4 grid grid-cols-2">
                {alternativeMedia.map((i, index) => (
                  <div key={index} className="gap-2 grid grid-cols-1">
                    <MediaAsset editable={false} value={i} />
                    <Button
                      onClick={() => {
                        onSelectMedia(i);
                        state.activeKey = undefined;
                      }}
                      type="primary"
                      block
                    >
                      Select
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <Alert
                type="info"
                message="No other media found in article for this segment."
              />
            )
          ),
        },
        {
          key: "2",
          label: "External library",
          children: (
            <div className="gap-3 grid grid-cols-1">
              <Input.Search
                value={state.searchText}
                onChange={(event) => {
                  state.searchText = event.target.value;
                }}
                onSearch={(value) => {
                  void loadExternalAssets(value);
                }}
                enterButton="Search"
                placeholder="Search people, places, or article keywords"
                loading={state.loading}
              />

              {state.searchError && <Alert type="error" message={state.searchError} showIcon />}
              {state.importError && <Alert type="error" message={state.importError} showIcon />}

              {state.loading && (
                <div className="py-8 w-full text-center">
                  <Spin />
                </div>
              )}

              {!state.loading && state.queried && state.assets.length === 0 && <Empty description="No external images found" />}

              {!state.loading && state.assets.length > 0 && (
                <div className="gap-4 grid grid-cols-2">
                  {state.assets.map((asset) => (
                    <div key={asset.external_asset_id} className="gap-2 grid grid-cols-1">
                      <img
                        src={asset.preview_url}
                        alt={asset.title || "External image"}
                        className="rounded-xl w-full aspect-square object-cover"
                      />
                      <div className="text-xs text-gray-600 line-clamp-2">
                        {asset.title || asset.description || asset.external_asset_id}
                      </div>
                      <Button
                        onClick={() => {
                          void handleImportAndSelect(asset);
                        }}
                        type="primary"
                        loading={state.importingAssetId === asset.external_asset_id}
                        block
                      >
                        Import and select
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ),
        },
      ]}
    />
  );
};

export default ReplaceMedia;
